import React, { useState, useEffect } from 'react';
import { getAllPrompts } from '../../prompts';
import './PromptManagement.css';

const PromptManagement = () => {
  const [prompts, setPrompts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('systemPrompts');
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [savedPrompts, setSavedPrompts] = useState({});

  useEffect(() => {
    // Load prompts
    const allPrompts = getAllPrompts();
    setPrompts(allPrompts);
    
    // Load saved prompts from localStorage
    const saved = localStorage.getItem('customPrompts');
    if (saved) {
      setSavedPrompts(JSON.parse(saved));
    }
  }, []);

  const handleEdit = (category, key, value) => {
    setEditingPrompt({ category, key });
    
    // Handle different function signatures for editing
    let editableValue;
    if (typeof value === 'function') {
      try {
        if (key === 'codeAnalysis') {
          editableValue = value('// Example code\nfunction example() {\n  return "Hello World";\n}', []);
        } else if (key === 'intentClassification') {
          editableValue = value('Optimize this code', '// Example code');
        } else if (key === 'optimizationTemplate') {
          editableValue = value('// Example code', 'Performance');
        } else {
          editableValue = value('ProductName');
        }
      } catch (error) {
        editableValue = 'Error rendering prompt template';
      }
    } else {
      editableValue = value;
    }
    
    setEditValue(editableValue);
  };

  const handleSave = () => {
    if (!editingPrompt) return;

    const { category, key } = editingPrompt;
    const newSavedPrompts = {
      ...savedPrompts,
      [category]: {
        ...savedPrompts[category],
        [key]: editValue
      }
    };
    
    setSavedPrompts(newSavedPrompts);
    localStorage.setItem('customPrompts', JSON.stringify(newSavedPrompts));
    setEditingPrompt(null);
    setEditValue('');
  };

  const handleCancel = () => {
    setEditingPrompt(null);
    setEditValue('');
  };

  const handleReset = (category, key) => {
    const newSavedPrompts = { ...savedPrompts };
    if (newSavedPrompts[category] && newSavedPrompts[category][key]) {
      delete newSavedPrompts[category][key];
      if (Object.keys(newSavedPrompts[category]).length === 0) {
        delete newSavedPrompts[category];
      }
    }
    setSavedPrompts(newSavedPrompts);
    localStorage.setItem('customPrompts', JSON.stringify(newSavedPrompts));
  };

  const exportPrompts = () => {
    const dataStr = JSON.stringify({ original: prompts, customized: savedPrompts }, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `prompts-backup-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const importPrompts = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          if (imported.customized) {
            setSavedPrompts(imported.customized);
            localStorage.setItem('customPrompts', JSON.stringify(imported.customized));
            alert('Prompts imported successfully!');
          }
        } catch (error) {
          alert('Error importing prompts: Invalid file format');
        }
      };
      reader.readAsText(file);
    }
  };

  const renderPromptValue = (value, category, key) => {
    const isEditing = editingPrompt?.category === category && editingPrompt?.key === key;
    const savedValue = savedPrompts[category]?.[key];
    
    // Handle different function signatures
    let displayValue = savedValue;
    if (!displayValue) {
      if (typeof value === 'function') {
        try {
          // Special handling for functions that need multiple parameters
          if (key === 'codeAnalysis') {
            displayValue = value('// Example code\nfunction example() {\n  return "Hello World";\n}', []);
          } else if (key === 'intentClassification') {
            displayValue = value('Optimize this code', '// Example code');
          } else if (key === 'optimizationTemplate') {
            displayValue = value('// Example code', 'Performance');
          } else {
            // Default single parameter functions
            displayValue = value('ProductName');
          }
        } catch (error) {
          displayValue = 'Error rendering prompt template';
        }
      } else {
        displayValue = value;
      }
    }

    if (isEditing) {
      return (
        <div className="edit-prompt">
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            rows={10}
            cols={80}
          />
          <div className="edit-actions">
            <button className="save-btn" onClick={handleSave}>Save</button>
            <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      );
    }

    return (
      <div className="prompt-value">
        <pre>{displayValue}</pre>
        {savedValue && <span className="customized-badge">Customized</span>}
        <div className="prompt-actions">
          <button 
            className="edit-btn" 
            onClick={() => handleEdit(category, key, value)}
          >
            Edit
          </button>
          {savedValue && (
            <button 
              className="reset-btn" 
              onClick={() => handleReset(category, key)}
            >
              Reset to Default
            </button>
          )}
          <button 
            className="copy-btn" 
            onClick={(e) => {
              navigator.clipboard.writeText(displayValue);
              const btn = e.target;
              const originalText = btn.textContent;
              btn.textContent = 'Copied!';
              setTimeout(() => {
                btn.textContent = originalText;
              }, 2000);
            }}
          >
            Copy
          </button>
        </div>
      </div>
    );
  };

  const categories = {
    systemPrompts: 'System Prompts',
    deploymentEmailTemplates: 'Deployment Email Templates',
    codeAssistantPrompts: 'Code Assistant Prompts',
    additionalContext: 'Additional Context',
    codePatterns: 'Code Patterns'
  };

  const filterPrompts = (promptsObj) => {
    if (!searchTerm) return promptsObj;
    
    const filtered = {};
    Object.entries(promptsObj).forEach(([key, value]) => {
      const valueStr = typeof value === 'function' ? value('ProductName') : String(value);
      if (key.toLowerCase().includes(searchTerm.toLowerCase()) || 
          valueStr.toLowerCase().includes(searchTerm.toLowerCase())) {
        filtered[key] = value;
      }
    });
    return filtered;
  };

  return (
    <div className="prompt-management">
      <div className="prompt-header">
        <h1>Prompt Management</h1>
        <div className="header-actions">
          <input
            type="text"
            placeholder="Search prompts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button className="export-btn" onClick={exportPrompts}>
            Export Prompts
          </button>
          <label className="import-btn">
            Import Prompts
            <input
              type="file"
              accept=".json"
              onChange={importPrompts}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      <div className="prompt-content">
        <div className="category-sidebar">
          {Object.entries(categories).map(([key, label]) => (
            <button
              key={key}
              className={`category-btn ${selectedCategory === key ? 'active' : ''}`}
              onClick={() => setSelectedCategory(key)}
            >
              {label}
              {savedPrompts[key] && (
                <span className="custom-count">
                  {Object.keys(savedPrompts[key]).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="prompts-list">
          {prompts[selectedCategory] && (
            <>
              <h2>{categories[selectedCategory]}</h2>
              <div className="prompts-grid">
                {Object.entries(filterPrompts(prompts[selectedCategory])).map(([key, value]) => (
                  <div key={key} className="prompt-item">
                    <h3>{key}</h3>
                    {renderPromptValue(value, selectedCategory, key)}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="prompt-info">
        <p>
          <strong>Note:</strong> Customized prompts are saved locally in your browser. 
          Use the export/import feature to backup or share your customizations.
        </p>
      </div>
    </div>
  );
};

export default PromptManagement;